import { useMemo } from 'react'

import { getTeamsById } from 'conversations/modules/teams/selectors'
import { getActiveOrgUsers } from 'conversations/modules/user/selectors'
import {
  createConditionsArray,
  getAllValuesOfType,
  HARDCODED_TEAMMATE_IDS,
  QUEUE_TIME_PROPERTY,
} from 'conversations/utils/conditionsUtil'
import { useSelector } from 'react-redux'
import {
  ConversationSidebarFilterCondition,
  ConversationSidebarSectionConfig,
} from 'shared/lib/typings/conversationsSidebar/Filters'
import { Team } from 'shared/typings/teams/Team'
import { User } from 'shared/typings/user/User'

type ConditionsType = ConversationSidebarFilterCondition[] | undefined

const getSimpleTypeFromCondition = (conditions: ConditionsType, type: string) => {
  const items = conditions?.map((condition) => getAllValuesOfType(type, condition)) || []
  return items.filter((item) => (Array.isArray(item) ? !!item.length : item))
}

const getParticipantsFromCondition = (
  conditions: ConditionsType,
  teams: Record<number, Team>,
  activeOrgUsers: User[]
) => {
  const participantIds =
    conditions?.map((condition) => getAllValuesOfType('participants', condition)) || []
  const getTeamsQuery = conditions?.map((condition) => getAllValuesOfType('team', condition)) || []
  getTeamsQuery.forEach((teamList) => {
    const members = teamList.flatMap((team) => teams[team]?.members || [])
    participantIds.push(members)
  })
  let filteredParticipants = participantIds.filter((arr) => !!arr.length)
  const flattenParticipants = filteredParticipants.flatMap((p) => p)
  if (flattenParticipants.includes(HARDCODED_TEAMMATE_IDS.ALL_PARTICIPANTS)) {
    filteredParticipants = [activeOrgUsers.map((user) => user.id)]
  }
  return filteredParticipants
}

const getRoutedNotRespondedTime = (conditions: ConditionsType) => {
  return getSimpleTypeFromCondition(conditions, QUEUE_TIME_PROPERTY).flatMap((t) => t)[0]
}

export const useSectionQueryGroups = (sectionConfig: ConversationSidebarSectionConfig) => {
  const teams = useSelector(getTeamsById)
  const activeOrgUsers = useSelector(getActiveOrgUsers)

  return useMemo(() => {
    const filterGroups = []
    const conditions = createConditionsArray(sectionConfig.condition)

    if (sectionConfig.condition.operator === 'or') {
      conditions.forEach((c) => {
        const cArr = createConditionsArray(c)
        filterGroups.push({
          tags: getSimpleTypeFromCondition(cArr, 'tags'),
          participantIds: getParticipantsFromCondition(cArr, teams, activeOrgUsers),
          routedNotRespondedTime: getRoutedNotRespondedTime(cArr),
        })
      })
    } else {
      filterGroups.push({
        tags: getSimpleTypeFromCondition(conditions, 'tags'),
        participantIds: getParticipantsFromCondition(conditions, teams, activeOrgUsers),
        routedNotRespondedTime: getRoutedNotRespondedTime(conditions),
      })
    }

    return filterGroups
  }, [sectionConfig.condition, teams, activeOrgUsers])
}
